import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { Outlet } from 'react-router'
import type { LiveEvent, Space, TodayView } from '@takeit4me/shared'
import type { LiveStatus } from '@takeit4me/sdk'
import { api } from '../api'
import { useQuery } from '../lib/hooks'
import { PhoneNav, Sidebar } from './Sidebar'
import { useIsPhone } from '../lib/media'
import { SwitchesProvider, useSwitchesQuery } from './Sending'
import { OnboardingBanner } from '../pages/platform/lifecycle'

/** Live events fan-out so any page can react to the one socket. */
type Listener = (e: LiveEvent) => void
const listeners = new Set<Listener>()
const LiveCtx = createContext<{ subscribe: (fn: Listener) => () => void; status: LiveStatus }>({ subscribe: (fn: Listener) => { listeners.add(fn); return () => { listeners.delete(fn) } }, status: 'connecting' })
export const useLiveEvents = () => useContext(LiveCtx)

/** Refetch helper: calls `fn` when a live event matches `filter`. */
export function useOnLiveEvent(filter: (e: LiveEvent) => boolean, fn: (e: LiveEvent) => void) {
  const { subscribe } = useLiveEvents()
  useEffect(() => subscribe((e) => { if (filter(e)) fn(e) }))
}

export function Layout() {
  const phone = useIsPhone()
  const today = useQuery<TodayView>(() => api.today(), [], { refreshMs: 60_000 })
  const switches = useSwitchesQuery()
  const [status, setStatus] = useState<LiveStatus>('connecting')
  useEffect(() => {
    const sub = api.subscribeLive((e) => {
      for (const l of listeners) l(e)
      if (e.type === 'switches') void switches.reload(true)
      if (e.type.startsWith('exception') || e.type.startsWith('call.') || e.type === 'task.updated' || e.type === 'alarm') void today.reload(true)
    }, setStatus)
    return () => sub.close()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const badges = useMemo<Partial<Record<Space, number>>>(() => {
    const t = today.data
    if (!t) return {}
    return {
      exceptions: t.needs_you.filter((n) => n.kind === 'exception').length,
      live: t.live.calls,
      tasks: t.needs_you.filter((n) => n.kind === 'task').length,
    }
  }, [today.data])
  const ctx = useMemo(() => ({ subscribe: (fn: Listener) => { listeners.add(fn); return () => { listeners.delete(fn) } }, status }), [status])
  return (
    <LiveCtx.Provider value={ctx}>
      <SwitchesProvider q={switches}>
        {/* At phone width (4-03, X-10; design 12.2) the sidebar becomes a compact top bar with a slide-over menu drawer. */}
        <div className={`flex h-screen h-[100dvh] w-screen overflow-hidden ${phone ? 'flex-col' : ''}`}>
          {phone ? <PhoneNav badges={badges} /> : <Sidebar badges={badges} />}
          <div className="flex-1 flex flex-col min-w-0 min-h-0">
            <OnboardingBanner />
            <Outlet />
          </div>
        </div>
      </SwitchesProvider>
    </LiveCtx.Provider>
  )
}
